import React from 'react';
import { InputNumber, Button } from 'antd';
import { Star, Edit } from 'lucide-react';
import { NumericFormat } from 'react-number-format';
import { fmtCur, fmtNum } from '../Common';

const NorthStarTab = ({ nsParams, setNsParams, syncInputs }) => {
    const { capital, targetReturn, totalYears, tradingDays, elapsedDays, pnl } = nsParams;

    const updateParam = (field, value) => {
        setNsParams(prev => ({ ...prev, [field]: value }));
    };

    const updateArrayParam = (field, index, value) => {
        const newArr = [...nsParams[field]];
        newArr[index] = value;
        setNsParams(prev => ({ ...prev, [field]: newArr }));
    };

    const startYear = new Date().getFullYear();
    let currentCapital = capital;
    const tableRows = [];

    for (let i = 0; i < totalYears; i++) {
        const year = startYear + i;
        const totalDaysYear = tradingDays[i] || 247;
        const totalReturnYear = (currentCapital * (targetReturn || 0)) / 100;
        const perDayTarget = totalReturnYear / totalDaysYear;

        const elapsed = elapsedDays[i] || 0;
        const currentPnl = pnl[i] || 0;
        const hasData = elapsed > 0 || currentPnl !== 0;

        let eoyPortfolio = currentCapital + totalReturnYear;
        let revisedPerDay = null;
        let expectedPnl = 0;
        let excess = 0;
        let aheadDays = 0;
        let drawdown = 0;
        let aheadPct = 0;

        if (hasData) {
            expectedPnl = perDayTarget * elapsed;
            revisedPerDay = (totalDaysYear - elapsed) > 0 ? (totalReturnYear - currentPnl) / (totalDaysYear - elapsed) : 0;
            excess = currentPnl - expectedPnl;
            aheadDays = perDayTarget !== 0 ? excess / perDayTarget : 0;
            drawdown = excess < 0 ? Math.abs(excess) : 0;
            aheadPct = expectedPnl !== 0 ? (excess / expectedPnl) * 100 : 0;

            const remainingDays = Math.max(0, totalDaysYear - elapsed);
            const remainingExpected = perDayTarget * remainingDays;
            eoyPortfolio = currentCapital + currentPnl + remainingExpected;
        }

        tableRows.push({
            year, index: i, totalDaysYear, elapsed, currentCapital, totalReturnYear, eoyPortfolio,
            perDayTarget, revisedPerDay, expectedPnl, currentPnl, excess, aheadDays, drawdown, aheadPct, hasData
        });

        currentCapital = eoyPortfolio;
    }

    return (
        <div className="nst-container">
            <div className="nst-header-panel">
                <div className="nst-panel-title"><Star size={18} /> North Star Execution Tracker</div>
                <div className="nst-input-row">
                    <div className="nst-input-group">
                        <div className="nst-input-label">Starting Capital</div>
                        <InputNumber
                            prefix="₹"
                            style={{ width: '200px' }}
                            value={capital}
                            onChange={(val) => syncInputs('ns', val || 0, 'capital')}
                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        />
                    </div>
                    <div className="nst-input-group">
                        <div className="nst-input-label">CAGR Target (%)</div>
                        <InputNumber
                            suffix="%"
                            style={{ width: '150px' }}
                            value={targetReturn}
                            onChange={(val) => syncInputs('ns', val || 0, 'targetReturn')}
                        />
                    </div>
                </div>
            </div>

            <div className="nst-table-container mt-4">
                <table className="nst-table">
                    <thead>
                        <tr>
                            <th>Year</th>
                            <th className="nst-col-input" style={{ width: '100px' }}>Total Trading Days <Edit size={12} /></th>
                            <th className="nst-col-input" style={{ width: '100px' }}>Elapsed Days <Edit size={12} /></th>
                            <th>Starting Capital (₹)</th>
                            <th>Total Return (₹)</th>
                            <th style={{ color: 'var(--blue)' }}>EOY Portfolio (₹)</th>
                            <th>Per Day Target (₹)</th>
                            <th>Revised Per Day Target (₹)</th>
                            <th>Profit Expectation Till Date (₹)</th>
                            <th className="nst-col-input" style={{ width: '120px' }}>Current PNL (₹) <Edit size={12} /></th>
                            <th>Excess Profit or Drawdown (₹)</th>
                            <th>Ahead/Behind Plan (Days)</th>
                            <th>Drawdown (₹)</th>
                            <th>Ahead/Behind Plan (%)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map((row) => (
                            <tr key={row.year}>
                                <td style={{ fontWeight: 700, color: 'var(--text2)', textAlign: 'left' }}>{row.year}</td>
                                <td className="nst-col-input">
                                    <input
                                        type="number"
                                        className="nst-native-input"
                                        value={row.totalDaysYear}
                                        onChange={(e) => updateArrayParam('tradingDays', row.index, parseFloat(e.target.value) || 0)}
                                    />
                                </td>
                                <td className="nst-col-input">
                                    <input
                                        type="number"
                                        className="nst-native-input"
                                        value={row.elapsed || ''}
                                        placeholder="0"
                                        onChange={(e) => updateArrayParam('elapsedDays', row.index, parseFloat(e.target.value) || 0)}
                                    />
                                </td>
                                <td>{fmtCur.format(row.currentCapital)}</td>
                                <td>{fmtCur.format(row.totalReturnYear)}</td>
                                <td className="val-blue" style={{ fontWeight: 700 }}>{fmtCur.format(row.eoyPortfolio)}</td>
                                <td>{fmtNum.format(row.perDayTarget)}</td>
                                <td>{row.hasData ? fmtNum.format(row.revisedPerDay) : '--'}</td>
                                <td>{row.hasData ? fmtNum.format(row.expectedPnl) : '--'}</td>
                                <td className="nst-col-input">
                                    <NumericFormat
                                        className={`nst-native-input ${row.hasData ? (row.currentPnl >= 0 ? 'val-positive' : 'val-negative') : ''}`}
                                        style={{ fontWeight: 700 }}
                                        value={row.currentPnl || ''}
                                        placeholder="0"
                                        thousandsGroupStyle="lakh"
                                        thousandSeparator=","
                                        decimalSeparator="."
                                        allowNegative={true}
                                        onValueChange={(values) => {
                                            updateArrayParam('pnl', row.index, values.floatValue || 0);
                                        }}
                                    />
                                </td>
                                <td className={row.hasData ? (row.excess >= 0 ? 'val-positive' : 'val-negative') : ''}>{row.hasData ? fmtNum.format(row.excess) : '--'}</td>
                                <td className={row.hasData ? (row.aheadDays >= 0 ? 'val-positive' : 'val-negative') : ''}>{row.hasData ? Math.round(row.aheadDays) : '--'}</td>
                                <td className="val-negative">{row.hasData ? fmtNum.format(row.drawdown) : '--'}</td>
                                <td className={row.hasData ? (row.aheadPct >= 0 ? 'val-positive' : 'val-negative') : ''}>{row.hasData ? row.aheadPct.toFixed(1) : '--'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="mt-4" style={{ textAlign: 'center' }}>
                <Button type="dashed" onClick={() => updateParam('totalYears', totalYears + 5)}>+ 5 More Years</Button>
            </div>

            <div className="nst-description-panel mt-4">
                <div className="nst-desc-header">
                    <h2 className="nst-desc-title">North Star Guidance</h2>
                </div>
                <span className="nst-desc-meta">Your Long Term Trading Compass</span>
                <div className="nst-desc-divider"></div>

                <p className="nst-desc-content">The North Star Guidance system acts as a trading compass. It translates your long term return goal into daily progress checkpoints, helping you stay aligned with your plan throughout the year.</p>
                <p className="nst-desc-content">Most traders focus only on daily profit and loss. The problem is that a single day does not indicate whether you are on track to achieve your yearly goal.</p>
                <p className="nst-desc-content">North Star Guidance solves this problem by continuously answering three questions:</p>
                <ul className="nst-desc-list">
                    <li>Where should my capital be today if I am on track</li>
                    <li>Where am I currently based on my actual performance</li>
                    <li>What adjustment is required to stay aligned with my target</li>
                </ul>

                {/* How it works */}
                <h4 className="nst-desc-section-title">How the North Star System Works</h4>
                <p className="nst-desc-content">The system takes three core inputs:</p>
                <ul className="nst-desc-list">
                    <li>Starting Capital</li>
                    <li>Target CAGR</li>
                    <li>Elapsed Trading Days</li>
                </ul>
                <p className="nst-desc-content">From these inputs, it calculates:</p>
                <ul className="nst-desc-list">
                    <li>Expected capital today</li>
                    <li>Required daily profit velocity</li>
                    <li>Actual performance relative to plan</li>
                </ul>
                <p className="nst-desc-content" style={{ color: 'var(--text)', fontWeight: 600 }}>This converts a long term goal into daily measurable progress.</p>

                {/* Example */}
                <h4 className="nst-desc-section-title">Example</h4>
                <div className="nst-desc-example-box">
                    <div className="nst-desc-example-grid">
                        <div>
                            <div className="nst-desc-example-label">Starting capital</div>
                            <div className="nst-desc-example-value">₹10,00,000</div>
                        </div>
                        <div>
                            <div className="nst-desc-example-label">Target CAGR</div>
                            <div className="nst-desc-example-value">20%</div>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <div className="nst-desc-example-label">Expected yearly capital</div>
                            <div className="nst-desc-example-value val-green" style={{ fontSize: 20 }}>₹12,00,000</div>
                        </div>
                    </div>
                    <p className="nst-desc-content" style={{ marginBottom: 8 }}>If there are 250 trading days in the year, the system calculates the daily progress required.</p>
                    <p className="nst-desc-content"><strong>Daily progress target:</strong> <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>₹800 per day</span></p>
                    <p className="nst-desc-content" style={{ marginBottom: 8 }}>If 125 days have passed, expected progress is:</p>
                    <p className="nst-desc-content" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 700 }}>₹1,00,000</p>
                    <p className="nst-desc-content" style={{ marginBottom: 8 }}>If current profit is:</p>
                    <p className="nst-desc-content" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 18, fontWeight: 700 }}>₹80,000</p>
                    <p className="nst-desc-content val-negative" style={{ fontWeight: 700 }}>You are behind plan by ₹20,000.</p>
                    <p className="nst-desc-content">The system then recalculates the new required daily velocity.</p>
                </div>

                {/* Column meanings */}
                <h4 className="nst-desc-section-title">What Each Column Means</h4>

                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Starting Capital</h5>
                    <p className="nst-desc-content">The capital at the beginning of the year. This becomes the base used for compounding calculations.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Total Return</h5>
                    <p className="nst-desc-content">The profit required to achieve the yearly CAGR goal.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}><em>Example:</em> 20% CAGR on ₹10L requires ₹2L annual profit.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">End Of Year Portfolio</h5>
                    <p className="nst-desc-content">Expected capital at the end of the year if the goal is achieved.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}><em>Example:</em> ₹10L → ₹12L.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Per Day Target</h5>
                    <p className="nst-desc-content">The daily profit required to achieve the goal.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}><em>Example:</em> ₹2L yearly profit across 250 trading days = ₹800 per day.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Revised Per Day Target</h5>
                    <p className="nst-desc-content">If you fall behind or move ahead of schedule, the system adjusts the remaining daily requirement.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}><em>Example:</em> If you are behind schedule, your required daily target increases slightly. If you are ahead, it decreases.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Profit Expectation Till Date</h5>
                    <p className="nst-desc-content">The profit that should have been achieved based on elapsed trading days.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}><em>Example:</em> After 125 days, Expected profit = ₹1L.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Current P&amp;L</h5>
                    <p className="nst-desc-content">The trader's real performance so far.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}>This is manually updated or automatically synced with trading logs.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Excess Profit or Drawdown</h5>
                    <p className="nst-desc-content">Difference between expected profit and actual profit.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}><em>Example:</em> Expected = ₹1L, Actual = ₹80K. Drawdown from plan = ₹20K.</p>
                </div>
                <div className="nst-desc-col-block">
                    <h5 className="nst-desc-col-title">Ahead or Behind Plan</h5>
                    <p className="nst-desc-content">Shows how many trading days you are ahead or behind your trajectory.</p>
                    <p className="nst-desc-content" style={{ fontSize: 14 }}><em>Example:</em> If you achieved profits faster than expected, you may be 20 days ahead of schedule.</p>
                </div>

                <div className="nst-desc-divider" style={{ marginTop: 32, marginBottom: 32 }}></div>

                {/* Why powerful */}
                <h4 className="nst-desc-section-title">Why This System Is Powerful</h4>
                <p className="nst-desc-content">Most traders judge success based on daily results. But trading performance must be evaluated relative to long term trajectory.</p>
                <div className="nst-desc-highlight-box">
                    <p className="nst-desc-content"><strong>For Example:</strong></p>
                    <p className="nst-desc-content">A trader may lose money today but still remain ahead of yearly plan.</p>
                    <p className="nst-desc-content" style={{ marginBottom: 0 }}>Another trader may make money today but still remain behind target.</p>
                </div>
                <p className="nst-desc-content" style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, textAlign: 'center', marginTop: 24 }}>
                    North Star Guidance provides the correct perspective.
                </p>
            </div>
        </div>
    );
};

export default NorthStarTab;